import ContextMenuController from "./context_menu_controller"

// Right-click context menu on image thumbnails for quick album management.
//
// Subclasses the base context-menu primitive: the base owns interception,
// viewport-aware positioning, the open/close lifecycle, and Turbo-frame
// loading. This controller supplies the album-specific bits — which image was
// clicked, the frame endpoint, and the add/remove fetch handlers.
//
// The menu content is server-rendered via the Turbo frame
// (`/albums_images/context_menu`) to follow the "no HTML in JS" convention.
//
// Targets are re-declared here so the DOM attributes resolve against this
// controller's registered identifier ("image-context-menu"). The markup uses
// data-image-context-menu-target="menu|loading|content|frame".
export default class extends ContextMenuController {
  static targets = ["menu", "frame", "loading", "content"]

  connect() {
    super.connect()
    // When a review action lands while this menu is open, the option list in
    // the frame (Mark as Under Review / Approved / Rejected / Clear) is rendered
    // against the OLD review_status and goes stale. The review-actions controller
    // dispatches a document-level "review:updated" event after a successful
    // submission — listen for it and reload the menu frame in place so the
    // options re-render against the new status. No reopen needed. (#1808)
    this.boundReloadFrame = this.reloadFrameInPlace.bind(this)
    document.addEventListener("review:updated", this.boundReloadFrame)
  }

  disconnect() {
    super.disconnect()
    if (this.boundReloadFrame) {
      document.removeEventListener("review:updated", this.boundReloadFrame)
      this.boundReloadFrame = null
    }
  }

  // Reload the menu's Turbo frame in place so the review option list re-renders
  // against the new status. Scoped to only fire when the menu is actually
  // visible — a background review elsewhere should not refetch a closed menu.
  // Re-wires the loading -> content swap (showLoading + the once-fire
  // turbo:frame-load handler from the base loadFrame) so the spinner shows
  // briefly and the refreshed content reveals correctly.
  reloadFrameInPlace() {
    if (!this.hasMenuTarget || this.menuTarget.classList.contains("hidden")) return
    if (!this.hasFrameTarget) return

    this.showLoading()
    this.frameTarget.addEventListener(
      "turbo:frame-load",
      this.handleFrameLoad.bind(this),
      { once: true }
    )
    this.frameTarget.reload()
  }

  // Resolve the clicked image and stash its id for frameSrc / fetch handlers.
  beforeOpen(event) {
    // Original path: images index / album show thumbnails
    const imageContainer = event.target.closest("[data-image-id]")
    if (imageContainer) {
      this.currentImageId = imageContainer.getAttribute("data-image-id")
      return true
    }
    // Gallery path: response preview — read from the displayed main image
    const mainImage = this.element.querySelector("[data-gallery-target='mainImage']")
    if (mainImage && mainImage.dataset.galleryImageId) {
      this.currentImageId = mainImage.dataset.galleryImageId
      return true
    }
    return false
  }

  frameSrc() {
    return `/albums_images/context_menu?image_id=${this.currentImageId}`
  }

  addToAlbum(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const albumId = button.dataset.albumId
    const imageId = button.dataset.imageId

    if (!albumId || !imageId) return

    // Disable button and show loading
    button.disabled = true
    button.classList.add("opacity-50")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch("/albums_images", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({
        albums_image: { image_id: imageId, album_id: albumId }
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Mark as added in the menu
          button.classList.remove("opacity-50")
          button.classList.add("text-green-600", "bg-green-50")
          button.disabled = true

          // Update the count badge text
          const countBadge = button.querySelector("[data-count]")
          if (countBadge) {
            const currentCount = parseInt(countBadge.textContent) || 0
            countBadge.textContent = currentCount + 1
          }

          // Add checkmark
          const checkmark = button.querySelector("[data-checkmark]")
          if (checkmark) checkmark.classList.remove("hidden")

          // Dispatch event for the album controller to update the image card UI
          this.dispatch("added", {
            detail: { imageId, albumId, albumData: data.album }
          })

          // Menu stays open so the user can add to multiple albums
        } else {
          button.disabled = false
          button.classList.remove("opacity-50")
          console.error("Failed to add image to album:", data.message)
        }
      })
      .catch(error => {
        button.disabled = false
        button.classList.remove("opacity-50")
        console.error("Error adding image to album:", error)
      })
  }

  removeFromAlbum(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const albumId = button.dataset.albumId
    const imageId = button.dataset.imageId

    if (!albumId || !imageId) return

    button.disabled = true
    button.classList.add("opacity-50")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch("/albums_images/remove", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify({ image_ids: imageId, album_id: albumId })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          button.classList.remove("opacity-50")
          button.classList.add("text-red-400", "line-through")
          button.disabled = true

          this.dispatch("removed", {
            detail: { imageId, albumId }
          })

          // Menu stays open so the user can remove and continue browsing
        } else {
          button.disabled = false
          button.classList.remove("opacity-50")
        }
      })
      .catch(() => {
        button.disabled = false
        button.classList.remove("opacity-50")
      })
  }

  filterAlbums(event) {
    const query = event.currentTarget.value.trim().toLowerCase()
    const frame = event.currentTarget.closest("turbo-frame")
    if (!frame) return
    frame.querySelectorAll("[data-album-filter]").forEach(btn => {
      const name = (btn.dataset.albumFilterName || "").toLowerCase()
      const id   = btn.dataset.albumFilterId || ""
      btn.style.display = (!query || name.includes(query) || id.includes(query)) ? "" : "none"
    })
  }

  copyImageUrl(event) {
    event.preventDefault()
    event.stopPropagation()
    const url = event.currentTarget.dataset.url
    if (!url) return

    const iconSlot  = event.currentTarget.querySelector("[data-copy-icon]")
    const origIcon  = iconSlot ? iconSlot.innerHTML : null

    navigator.clipboard.writeText(url).then(() => {
      if (iconSlot) {
        iconSlot.innerHTML = `<svg class="size-4 shrink-0 text-green-500 stroke-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>`
      }
      setTimeout(() => {
        if (iconSlot && origIcon) iconSlot.innerHTML = origIcon
      }, 1500)
    }).catch(() => {
      // silent fail — URL stays in whatever state it was
    })
  }

  copyImage(event) {
    event.preventDefault()
    event.stopPropagation()

    const button   = event.currentTarget
    const src      = button.dataset.src
    if (!src) return

    const iconSlot = button.querySelector("[data-copy-icon]")
    const origIcon = iconSlot ? iconSlot.innerHTML : null
    const swapIcon = (path, colour) => {
      if (!iconSlot) return
      iconSlot.innerHTML = `<svg class="size-4 shrink-0 ${colour} stroke-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${path}"/>
      </svg>`
      setTimeout(() => { if (iconSlot && origIcon) iconSlot.innerHTML = origIcon }, 1500)
    }

    navigator.clipboard
      .write([new ClipboardItem({ "image/png": this.imageToPngBlob(src) })])
      .then(() => swapIcon("M5 13l4 4L19 7", "text-green-500"))
      .catch(() => swapIcon("M6 18 18 6M6 6l12 12", "text-red-500"))
  }

  // Fetch the image via the same-origin download proxy and normalise to a PNG
  // blob — the Clipboard API only reliably accepts image/png. Same-origin means
  // no canvas taint when converting non-PNG sources.
  async imageToPngBlob(src) {
    const response = await fetch(src)
    const blob = await response.blob()
    if (blob.type === "image/png") return blob

    const bitmap = await createImageBitmap(blob)
    const canvas = document.createElement("canvas")
    canvas.width  = bitmap.width
    canvas.height = bitmap.height
    canvas.getContext("2d").drawImage(bitmap, 0, 0)
    return new Promise((resolve, reject) =>
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png")
    )
  }

}
